
import * as React from "react"
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"
import { useThemeConfig } from "@/components/ui/theme-config-provider"
import { useModalBackButton } from "@/hooks/useModalBackButton"

const AlertDialog = AlertDialogPrimitive.Root

const AlertDialogTrigger = AlertDialogPrimitive.Trigger

const AlertDialogPortal = AlertDialogPrimitive.Portal

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(( { className, ...props }, ref) => (
  // Ensure confirmation dialogs appear above parent modals
  <AlertDialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-[6000] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
))
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, ...props }, ref) => {
  // Extract id for modalName, using type assertion to access onOpenChange
  const { id } = props;
  const modalName = id || "alert-dialog";

  // Type-safe extraction of onOpenChange
  const onOpenChange = props['onOpenChange' as keyof typeof props] as 
    ((open: boolean) => void) | undefined;
  
  const backButtonHandler = useModalBackButton({
    isOpen: true, // il componente esiste solo se è aperto
    modalName,
    onOpenChange: (open) => {
      if (onOpenChange) {
        onOpenChange(open);
      }
    }
  });
  
  // Modify props by creating a new object
  const enhancedProps = {
    ...props,
    onOpenChange: backButtonHandler.handleOpenChange
  };

  const { onOpenChange: _, ...safeProps } = enhancedProps;

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cn(
          // z-index higher than parent dialogs so actions modal is visible
          "fixed left-[50%] top-[50%] z-[6001] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 max-h-[100dvh] overflow-y-auto pb-safe data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        {...safeProps}
      />
    </AlertDialogPortal>
  );
});
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName

const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
AlertDialogHeader.displayName = "AlertDialogHeader"

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
AlertDialogFooter.displayName = "AlertDialogFooter"

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold", className)}
    {...props}
  />
))
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName

interface AlertDialogActionProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>,
    "style"
  > {
  variant?: "default" | "destructive"
  style?: React.CSSProperties & Record<string, string>
}

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  AlertDialogActionProps
>(({ className, variant = "default", style, ...props }, ref) => {
  const { theme } = useThemeConfig()

  const baseStyle: React.CSSProperties & Record<string, string> =
    variant === "destructive"
      ? {
          backgroundColor: theme.errorColor || "#dc2626",
          color: theme.textPrimaryColor,
          borderRadius: theme.buttonBorderRadius,
          padding: theme.buttonPadding,
          fontWeight: theme.buttonFontWeight,
          transitionDuration: theme.transitionSpeed,
          ["--btn-bg-hover"]: theme.errorColorDark || theme.errorColor,
        }
      : {
          backgroundColor: theme.primaryColor,
          color: theme.textPrimaryColor || theme.textColor,
          borderRadius: theme.buttonBorderRadius,
          padding: theme.buttonPadding,
          fontWeight: theme.buttonFontWeight,
          transitionDuration: theme.transitionSpeed,
          ["--btn-bg-hover"]: theme.primaryColor,
        }

  return (
    <AlertDialogPrimitive.Action
      ref={ref}
      className={cn(
        buttonVariants({ variant: variant === "destructive" ? "destructive" : "default" }),
        "hover:bg-[var(--btn-bg-hover)]",
        className
      )}
      style={{
        ...baseStyle,
        ...style,
      }}
      {...props}
    />
  )
})
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  Omit<
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>,
    "style"
  > & {
    style?: React.CSSProperties & Record<string, string>
  }
>(({ className, style, ...props }, ref) => {
  const { theme } = useThemeConfig()

  const baseStyle: React.CSSProperties & Record<string, string> = {
    backgroundColor: theme.backgroundColor,
    color: theme.textColor,
    borderRadius: theme.buttonBorderRadius,
    padding: theme.buttonPadding,
    fontWeight: theme.buttonFontWeight,
    transitionDuration: theme.transitionSpeed,
    ["--btn-bg-hover"]: theme.primaryColor,
  }

  return (
    <AlertDialogPrimitive.Cancel
      ref={ref}
      className={cn(
        buttonVariants({ variant: "outline" }),
        "mt-2 sm:mt-0 hover:bg-[var(--btn-bg-hover)]",
        className
      )}
      style={{
        ...baseStyle,
        ...style,
      }}
      {...props}
    />
  )
})
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
}
